import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScheduleConflictItem } from "@myapp/contract";
import PageLayout from "../../shared/ui/layouts/PageLayout/PageLayout";
import { PATHS } from "../../app/routes/paths";
import { useCreateAbsence, getAbsenceCategoryLabel } from "../../entities/absence";
import {
  TimeManagementFormWidget,
  toCreateDto,
  type TimeManagementFormValues,
} from "../../features/time-management";
import { useSuccessDialog } from "../../shared/hooks/useSuccessDialog";
import { SuccessDialog } from "../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { ConflictDialog } from "../../shared/ui/feedback/ConflictDialog/ConflictDialog";
import { BlockerDialog } from "../../shared/ui/feedback/BlockerDialog/BlockerDialog";
import { parseConflictsFromError } from "../../shared/lib/errors/parseConflictsFromError";
import { parseCompletedBlockersFromError } from "../../shared/lib/errors/parseCompletedBlockersFromError";
import { useAuthStore } from "../../features/auth";

const CATEGORY = "PERSONAL_LEAVE" as const;

export const WorkerPrivatzeitPage = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const createMutation = useCreateAbsence();
  const submitRef = useRef<(() => void) | null>(null);
  const { showSuccess, dialogProps } = useSuccessDialog();

  const [conflicts, setConflicts] = useState<ScheduleConflictItem[] | null>(null);
  const [blockers, setBlockers] = useState<ScheduleConflictItem[] | null>(null);
  const pendingValuesRef = useRef<TimeManagementFormValues | null>(null);

  if (!user) return null;

  const initialValues: TimeManagementFormValues = {
    technicianIds: [Number(user.id)],
    category: CATEGORY,
    comment: "",
    dateFrom: "",
    dateTo: "",
    date: "",
    timeFrom: "",
    timeTo: "",
  };

  const handleSubmit = async (values: TimeManagementFormValues, force = false) => {
    try {
      const dto = toCreateDto(values, force);
      await createMutation.mutateAsync(dto);
      showSuccess("Erfolgreich gespeichert", () => navigate(PATHS.workerCalendar));
    } catch (err) {
      const blocking = parseCompletedBlockersFromError(err);
      if (blocking) {
        setBlockers(blocking);
        return;
      }
      const detected = parseConflictsFromError(err);
      if (detected) {
        pendingValuesRef.current = values;
        setConflicts(detected);
      } else {
        throw err;
      }
    }
  };

  const handleConflictConfirm = async () => {
    if (!pendingValuesRef.current) return;
    await handleSubmit(pendingValuesRef.current, true);
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  const handleConflictCancel = () => {
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  return (
    <>
      <PageLayout
        pageTitle={getAbsenceCategoryLabel(CATEGORY) + " Planung"}
        scrollable
        back
        backTo={PATHS.workerCalendar}
        primaryAction={{
          label: "Speichern",
          onClick: () => submitRef.current?.(),
          loading: createMutation.isPending,
        }}
      >
        <TimeManagementFormWidget
          category={CATEGORY}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
          submitRef={submitRef}
          initialValues={initialValues}
          hideTechnicianSelect
        />
      </PageLayout>
      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => { if (!open) dialogProps.onClose(); }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
      <ConflictDialog
        open={conflicts !== null}
        conflicts={conflicts ?? []}
        onConfirm={handleConflictConfirm}
        onCancel={handleConflictCancel}
        isLoading={createMutation.isPending}
      />
      <BlockerDialog
        open={blockers !== null}
        blockers={blockers ?? []}
        onClose={() => setBlockers(null)}
        onGoToReschedule={() => navigate(PATHS.reschedule)}
      />
    </>
  );
};
